import { useState, useEffect, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    TextField,
    Grid,
    Box,
    Typography,
    Chip,
    alpha,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    Divider,
    IconButton,
    FormControl,
    InputLabel,
    OutlinedInput,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { Search, Close, Check, SportsScore, Sports } from '@mui/icons-material';
import { AppContext } from '../all';

// ============================================================================
// Types
// ============================================================================

type Sport = {
    id: number;
    name: string;
    // CU-869egchpe: indica si el deporte admite partidos. Debe viajar hasta el
    // consumidor para que pueda limpiar la configuración de partidos cuando se
    // cambia a un deporte que no los admite.
    isForGame?: boolean | null;
};

type TranslatedSport = Sport & {
    translatedName: string;
};

// Props for autocomplete mode (inline dropdown)
type AutocompleteProps = {
    mode?: 'autocomplete';
    grid?: boolean;
    caption: string;
    name: string;
    defVal: number;
    size?: 'small' | 'medium';
    variant?: 'outlined' | 'filled' | 'standard';
    disabled?: boolean;
    onChange: (event: { name: string; value: unknown }) => void;
    [key: string]: unknown;
};

// Props for dialog mode (modal with grid of sports)
type DialogProps = {
    mode: 'dialog';
    fullWidth?: boolean;
    style?: React.CSSProperties;
    defVal?: string;
    onChange?: (sport: Sport) => void;
};

type Props = AutocompleteProps | DialogProps;

// ============================================================================
// Sport Colors (matching original SttSportSelector)
// ============================================================================

const sportColors: Record<number, string> = {
    // Sports with icons
    1: '#4CAF50', // Tennis - Green
    2: '#2196F3', // Padel - Blue
    3: '#FF5722', // Football 5 - Deep Orange
    4: '#E91E63', // Football 7 - Pink
    5: '#9C27B0', // Football - Purple
    6: '#8BC34A', // Golf - Light Green
    28: '#607D8B', // Multi-use - Blue Grey
    29: '#FF9800', // Basketball - Orange
    30: '#795548', // Handball - Brown
    31: '#00BCD4', // Badminton - Cyan
    32: '#FFEB3B', // Volleyball - Yellow
    33: '#03A9F4', // Pool Lane - Light Blue
    34: '#0288D1', // Pool - Dark Blue
    35: '#673AB7', // Squash - Deep Purple
    60: '#F44336', // Pilates - Red
    70: '#00ACC1', // Swimming - Cyan Dark
    72: '#7E57C2', // Yoga - Purple Light
    112: '#EC407A', // Zumba - Pink Light
    128: '#26A69A', // Pickleball - Teal
    // Sports without icons (fallback colors)
    36: '#FFC107', // Beach Volleyball - Amber
    37: '#FF7043', // Beach Football - Deep Orange Light
    38: '#D32F2F', // Karate - Red Dark
    39: '#8D6E63', // Petanque - Brown Light
    51: '#5C6BC0', // Fronton - Indigo Light
    53: '#78909C', // Agorespace - Blue Grey Light
    56: '#66BB6A', // Table Tennis - Green Light
    57: '#26C6DA', // Physiotherapy - Cyan Light
    59: '#FFA726', // Long Jump - Orange Light
    61: '#AB47BC', // Maintenance Gym - Purple
    62: '#EF5350', // Aerobic - Red Light
    63: '#42A5F5', // Gym - Blue Light
    64: '#FFCA28', // Minibasket - Amber Light
    65: '#81C784', // Hiking - Green Medium
    66: '#4DB6AC', // Cycling Tourism - Teal Light
    67: '#FF8A65', // Athletics - Deep Orange Light
    68: '#BA68C8', // Rhythmic Gymnastics - Purple Light
    69: '#4DD0E1', // Cycling - Cyan Medium
    71: '#7CB342', // Indoor Cycling - Light Green Dark
    73: '#5D4037', // Bodybuilding - Brown Dark
    74: '#1E88E5', // Hockey - Blue
    75: '#43A047', // Roller Hockey - Green
    76: '#8E24AA', // Climbing - Purple Dark
    77: '#0097A7', // Rowing - Cyan Dark
    78: '#00796B', // Canoeing - Teal Dark
    79: '#C0CA33', // Skating - Lime
    80: '#757575', // RC Cars - Grey
    81: '#D84315', // Baseball - Deep Orange Dark
    82: '#6D4C41', // Wrestling - Brown Medium
    83: '#546E7A', // Skate - Blue Grey Dark
    85: '#00BFA5', // Synchronized Swimming - Teal
    86: '#1565C0', // Waterpolo - Blue Dark
    88: '#E53935', // Judo - Red
    91: '#F06292', // Aero-Step - Pink Medium
    92: '#80CBC4', // Tai Chi - Teal Light
    94: '#424242', // Self Defense - Grey Dark
    95: '#9575CD', // Aikido - Deep Purple Light
    97: '#C62828', // Taekwondo - Red Dark
    100: '#CE93D8', // Tumbling - Purple Light
    101: '#F48FB1', // Dance - Pink Light
    102: '#90A4AE', // Senior Gymnastics - Blue Grey Medium
    103: '#AFB42B', // Spinning - Lime Dark
    104: '#4FC3F7', // Aquagym - Light Blue
    105: '#FF8F00', // Sauna - Amber Dark
    106: '#0277BD', // Sailing - Light Blue Dark
    108: '#B71C1C', // Kendo - Red Very Dark
    109: '#FDD835', // Kung-Fu - Yellow
    110: '#D50000', // Martial Arts - Red Accent
    111: '#BF360C', // Boxing - Deep Orange Very Dark
    113: '#FF6E40', // Football 3x3 - Deep Orange Accent
    114: '#DD2C00', // Kick Boxing - Deep Orange Dark
    115: '#2E7D32', // Tennis Doubles - Green Dark
    116: '#3949AB', // Double Fronton - Indigo
    117: '#558B2F', // Rugby - Light Green Dark
    118: '#37474F', // Chess - Blue Grey Very Dark
    119: '#FF5252', // Futvoley - Red Accent
    120: '#448AFF', // Padbol - Blue Accent
    121: '#6A1B9A', // Horse Riding - Purple Very Dark
    122: '#00897B', // Beach Tennis - Teal
    123: '#689F38', // Orienteering - Light Green Medium
    124: '#B3E5FC', // Ski - Light Blue Very Light
    125: '#455A64', // Parkour - Blue Grey
    126: '#E040FB', // Acrobatic Gymnastics - Purple Accent
    127: '#7C4DFF', // Trampoline - Deep Purple Accent
    129: '#FF4081', // Directed Activities - Pink Accent
    130: '#00E676', // Personal Training - Green Accent
    131: '#BDBDBD', // Pavilion - Grey Light
    132: '#FFAB40', // Playroom - Orange Accent
    133: '#69F0AE', // Jumping - Green Accent Light
    134: '#536DFE', // TRX - Indigo Accent
    136: '#FF6D00', // FIT Training - Orange Dark
    137: '#78909C', // Study Room - Blue Grey Light
    138: '#880E4F', // Archery - Pink Very Dark
    139: '#B388FF', // Body Jump - Deep Purple Accent Light
    140: '#82B1FF', // Full Body - Blue Accent Light
    141: '#EA80FC', // Muscle Toning - Purple Accent Light
    143: '#64DD17', // Ping Pong - Light Green Accent
};

const defaultColor = '#9E9E9E';

const getSportColor = (sportId: number): string =>
    sportColors[sportId] || defaultColor;

// ============================================================================
// Utility Functions
// ============================================================================

const normalizeText = (text: string | null | undefined): string => {
    if (!text) return '';
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
};

const customFilterOptions = (
    options: TranslatedSport[],
    state: { inputValue: string },
): TranslatedSport[] => {
    const normalizedInput = normalizeText(state.inputValue);
    if (!normalizedInput) return options;
    return options.filter((option) =>
        normalizeText(option.translatedName).includes(normalizedInput),
    );
};

// Module level cache
let cachedSports: Sport[] = [];

// ============================================================================
// Shared Components
// ============================================================================

type SportIconProps = {
    sportId: number;
    size?: 'small' | 'medium' | 'large';
};

const SportIcon = ({ sportId, size = 'medium' }: SportIconProps) => {
    const [hasError, setHasError] = useState(false);
    const dimensions = { small: 20, medium: 28, large: 36 };
    const iconSizes = { small: 16, medium: 22, large: 28 };
    const color = getSportColor(sportId);

    return (
        <Box
            sx={{
                width: dimensions[size],
                height: dimensions[size],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}
        >
            {hasError ? (
                <Sports
                    sx={{
                        fontSize: iconSizes[size],
                        color,
                        opacity: 0.8,
                    }}
                />
            ) : (
                <img
                    src={`/sports/${sportId}.png`}
                    alt=""
                    style={{
                        width: dimensions[size],
                        height: dimensions[size],
                        objectFit: 'contain',
                    }}
                    onError={() => setHasError(true)}
                />
            )}
        </Box>
    );
};

// ============================================================================
// Dialog Mode Component
// ============================================================================

type SportDialogProps = {
    open: boolean;
    sports: TranslatedSport[];
    selectedId?: number;
    onSelect: (sport: TranslatedSport) => void;
    onClose: () => void;
};

function SportDialog({
    open,
    sports,
    selectedId,
    onSelect,
    onClose,
}: SportDialogProps): React.ReactElement {
    const { t } = useTranslation();
    const [search, setSearch] = useState('');

    const filteredSports = useMemo(() => {
        if (!search) return sports;
        const normalized = normalizeText(search);
        return sports.filter((sport) =>
            normalizeText(sport.translatedName).includes(normalized),
        );
    }, [sports, search]);

    // Reset search when dialog opens
    useEffect(() => {
        if (open) setSearch('');
    }, [open]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2, minHeight: '60vh', maxHeight: '80vh' },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    pb: 1,
                }}
            >
                <Typography variant="h6" component="span" fontWeight={600}>
                    {t('SelectSport')}
                </Typography>
                <TextField
                    size="small"
                    placeholder={t('Search')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search color="action" />
                                </InputAdornment>
                            ),
                        },
                    }}
                    sx={{ flex: 1, maxWidth: 300 }}
                    autoFocus
                />
                <IconButton onClick={onClose} size="small">
                    <Close />
                </IconButton>
            </DialogTitle>

            <Divider />

            {/* Sports Grid */}
            <DialogContent sx={{ pt: 2 }}>
                {filteredSports.length === 0 ? (
                    <Box
                        sx={{
                            py: 6,
                            textAlign: 'center',
                            color: 'text.secondary',
                        }}
                    >
                        <SportsScore
                            sx={{ fontSize: 48, opacity: 0.3, mb: 1 }}
                        />
                        <Typography>{t('NoResults')}</Typography>
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        {filteredSports.map((sport) => {
                            const color = getSportColor(sport.id);
                            const isSelected = selectedId === sport.id;

                            return (
                                <Grid
                                    key={sport.id}
                                    size={{ xs: 6, sm: 4, md: 3 }}
                                >
                                    <Box
                                        onClick={() => {
                                            onSelect(sport);
                                            onClose();
                                        }}
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 2,
                                            border: '1px solid',
                                            borderColor: isSelected
                                                ? color
                                                : 'divider',
                                            backgroundColor: isSelected
                                                ? alpha(color, 0.08)
                                                : 'background.paper',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            position: 'relative',
                                            '&:hover': {
                                                borderColor: 'primary.dark',
                                                boxShadow: 1,
                                                transform: 'translateY(-2px)',
                                            },
                                        }}
                                    >
                                        {isSelected && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 6,
                                                    right: 6,
                                                    width: 18,
                                                    height: 18,
                                                    borderRadius: '50%',
                                                    backgroundColor: color,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Check
                                                    sx={{
                                                        fontSize: 12,
                                                        color: 'white',
                                                    }}
                                                />
                                            </Box>
                                        )}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: 0.5,
                                            }}
                                        >
                                            <SportIcon
                                                sportId={sport.id}
                                                size="large"
                                            />
                                            <Typography
                                                variant="caption"
                                                fontWeight={
                                                    isSelected ? 600 : 500
                                                }
                                                textAlign="center"
                                                sx={{
                                                    color: isSelected
                                                        ? color
                                                        : 'text.primary',
                                                    lineHeight: 1.2,
                                                }}
                                            >
                                                {sport.translatedName}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </DialogContent>
        </Dialog>
    );
}

// ============================================================================
// Main Component
// ============================================================================

export function SttSelectSport(props: Props): React.ReactElement {
    const { t } = useTranslation();
    const cxt = useContext(AppContext)!;
    const [sports, setSports] = useState<Sport[]>(cachedSports);
    const [dialogOpen, setDialogOpen] = useState(false);

    const mode = 'mode' in props ? props.mode : 'autocomplete';

    useEffect(() => {
        if (cachedSports.length === 0) {
            loadSports();
        }
    }, []);

    const loadSports = () => {
        cxt?.api('GET', `/sports`, {
            params: { page: 1, rows: 500 },
            success: (r: { rows: Sport[] }) => {
                cachedSports = r.rows;
                setSports(r.rows);
            },
        });
    };

    const translatedSports: TranslatedSport[] = useMemo(
        () =>
            sports.map((sport) => ({
                ...sport,
                translatedName: t(`sports.${sport.id}`, {
                    defaultValue: sport.name,
                }),
            })),
        [sports, t],
    );

    // ========================================================================
    // Dialog Mode
    // ========================================================================
    if (mode === 'dialog') {
        const dialogProps = props as DialogProps;
        const selectedSport = translatedSports.find(
            (s) => s.translatedName === dialogProps.defVal,
        );

        const handleSelect = (sport: TranslatedSport) => {
            // Se reenvía la entidad completa (no un literal {id, name}) para no
            // perder campos como `isForGame`. `name` conserva el nombre
            // traducido porque los consumidores lo usan como `defVal`.
            const { translatedName, ...sportData } = sport;
            dialogProps.onChange?.({ ...sportData, name: translatedName });
        };

        return (
            <Box style={dialogProps.style}>
                <FormControl
                    variant="outlined"
                    size="small"
                    onClick={() => setDialogOpen(true)}
                    style={{ width: dialogProps.fullWidth ? '100%' : 'auto' }}
                    sx={{ cursor: 'pointer' }}
                >
                    <InputLabel sx={{ cursor: 'pointer' }}>
                        {t('Sport')}
                    </InputLabel>
                    <OutlinedInput
                        readOnly
                        inputProps={{
                            style: { cursor: 'pointer' },
                        }}
                        type="text"
                        value={dialogProps.defVal || ''}
                        endAdornment={
                            <InputAdornment
                                position="end"
                                sx={{ cursor: 'pointer' }}
                            >
                                <Search />
                            </InputAdornment>
                        }
                        label={t('Sport')}
                        sx={{
                            cursor: 'pointer',
                            '& input': { cursor: 'pointer' },
                        }}
                    />
                </FormControl>
                <SportDialog
                    open={dialogOpen}
                    sports={translatedSports}
                    selectedId={selectedSport?.id}
                    onSelect={handleSelect}
                    onClose={() => setDialogOpen(false)}
                />
            </Box>
        );
    }

    // ========================================================================
    // Autocomplete Mode
    // ========================================================================
    const autocompleteProps = props as AutocompleteProps;
    const {
        grid = false,
        caption,
        name,
        defVal,
        size = 'small',
        variant = 'outlined',
        disabled = false,
        onChange,
        ...rest
    } = autocompleteProps;

    const selectedSport =
        translatedSports.find((sport) => sport.id === defVal) || null;

    const handleChange = (
        _event: React.SyntheticEvent,
        sport: TranslatedSport | null,
    ) => {
        onChange({ name, value: sport?.id ?? null });
    };

    const renderOption = (
        optionProps: React.HTMLAttributes<HTMLLIElement> & { key?: React.Key },
        option: TranslatedSport,
    ) => {
        const { key, ...otherProps } = optionProps;
        const color = getSportColor(option.id);
        const isSelected = selectedSport?.id === option.id;

        return (
            <Box
                component="li"
                key={key}
                {...otherProps}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1,
                    px: 1.5,
                    borderRadius: '8px',
                    mx: 0.5,
                    my: 0.25,
                    transition: 'all 0.15s ease',
                    backgroundColor: isSelected
                        ? alpha(color, 0.1)
                        : 'transparent',
                    '&:hover': {
                        backgroundColor: alpha(color, 0.08),
                    },
                }}
            >
                <SportIcon sportId={option.id} size="small" />
                <Typography
                    variant="body2"
                    sx={{
                        flex: 1,
                        fontWeight: isSelected ? 600 : 500,
                        color: isSelected ? color : 'text.primary',
                    }}
                >
                    {option.translatedName}
                </Typography>
                {isSelected && <Check sx={{ fontSize: 16, color }} />}
            </Box>
        );
    };

    const SelectedChip = () => {
        if (!selectedSport) return null;
        const color = getSportColor(selectedSport.id);

        return (
            <Chip
                size="small"
                icon={
                    <Box
                        sx={{ ml: 0.5, display: 'flex', alignItems: 'center' }}
                    >
                        <SportIcon sportId={selectedSport.id} size="small" />
                    </Box>
                }
                label={selectedSport.translatedName}
                sx={{
                    height: 26,
                    backgroundColor: alpha(color, 0.1),
                    border: `1px solid ${alpha(color, 0.25)}`,
                    color,
                    fontWeight: 500,
                    fontSize: '0.8rem',
                    '& .MuiChip-icon': { marginLeft: '4px' },
                    '& .MuiChip-label': { px: 1 },
                }}
            />
        );
    };

    const content = (
        <Autocomplete
            disabled={disabled}
            value={selectedSport}
            options={translatedSports}
            getOptionLabel={(option) => option.translatedName || ''}
            filterOptions={customFilterOptions}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={handleChange}
            renderOption={renderOption}
            slotProps={{
                paper: {
                    elevation: 8,
                    sx: {
                        borderRadius: '12px',
                        mt: 0.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        '& .MuiAutocomplete-listbox': {
                            py: 1,
                            maxHeight: 280,
                        },
                    },
                },
                listbox: {
                    sx: {
                        '& .MuiAutocomplete-option': {
                            padding: 0,
                            minHeight: 'auto',
                        },
                        '& .MuiAutocomplete-option[aria-selected="true"]': {
                            backgroundColor: 'transparent',
                        },
                        '& .MuiAutocomplete-option.Mui-focused': {
                            backgroundColor: 'transparent',
                        },
                    },
                },
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    fullWidth
                    size={size}
                    label={caption}
                    variant={variant}
                    placeholder={t('Search')}
                    slotProps={{
                        input: {
                            ...params.InputProps,
                            startAdornment: selectedSport ? (
                                <InputAdornment position="start" sx={{ ml: 0 }}>
                                    <SelectedChip />
                                </InputAdornment>
                            ) : (
                                <InputAdornment position="start">
                                    <Search
                                        sx={{
                                            fontSize: 20,
                                            color: 'text.disabled',
                                            ml: 0.5,
                                        }}
                                    />
                                </InputAdornment>
                            ),
                        },
                        inputLabel: { shrink: true },
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                            transition: 'all 0.2s ease',
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'primary.dark',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderWidth: 2,
                            },
                        },
                        '& .MuiInputBase-input': {
                            py: selectedSport ? 0.75 : 1,
                        },
                    }}
                />
            )}
        />
    );

    return grid ? <Grid {...rest}>{content}</Grid> : content;
}

// ============================================================================
// Legacy Export for Backwards Compatibility
// ============================================================================

type LegacySportSelectorProps = {
    fullWidth?: boolean;
    style?: React.CSSProperties;
    onChange?: (sport: Sport) => void;
    defVal?: string;
};

/**
 * @deprecated Use SttSelectSport with mode="dialog" instead
 */
export function SttSportSelector(
    props: LegacySportSelectorProps,
): React.ReactElement {
    return <SttSelectSport mode="dialog" {...props} />;
}
